import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { SupportEmailLink } from "components/app/support-email-link";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { ContentContainer, PageHeader, Stage } from "components/layout";
import { InlineBanner } from "components/ui/inline-banner";
import { useGetRecentConversationFeedForUserQuery } from "generated/graphql";
import React from "react";
import { Link, useParams } from "react-router-dom";
import { useFeature, useRegionId } from "store/selectors/hooks";
import { usePhoneLines } from "store/selectors/telecom";
import { ConversationFeed } from "./conversation-feed";
import { ConversationFeedLegacy } from "./conversation-feed-legacy";
import { MailboxConnectionState } from "./mailbox-connection-state";
import { useUserMailboxes } from "./use-user-mailboxes";

type MailboxChecklistState = MailboxConnectionState | "sync-disabled";

const MessagingSetupChecklist: React.FC<{
    phoneLinesConfigured: boolean;
    mailboxChecklistState: MailboxChecklistState;
}> = ({ phoneLinesConfigured, mailboxChecklistState }) => {
    const mailboxConnected = mailboxChecklistState === "connected";
    return (
        <InlineBanner type="info" showIcon={false}>
            <p className="font-medium mb-3">
                Get started with messaging by setting up your communication channels:
            </p>
            <ul className="space-y-2">
                <li className="flex items-start gap-2">
                    <CheckCircleIcon
                        className={`size-5 mt-0.5 shrink-0 ${phoneLinesConfigured ? "text-green-500" : "text-blue-300"}`}
                    />
                    <Link
                        to="/settings/account/phone"
                        className={
                            phoneLinesConfigured
                                ? "text-blue-400 line-through"
                                : "text-blue-800 underline"
                        }>
                        Set up a phone line to send and receive text messages
                    </Link>
                </li>
                {mailboxChecklistState === "sync-disabled" ? (
                    <li className="flex items-start gap-2">
                        <CheckCircleIcon className="size-5 mt-0.5 shrink-0 text-blue-300" />
                        <span>
                            We&#39;re rolling out Mailbox Sync! Reach out to{" "}
                            <SupportEmailLink
                                label="Support"
                                subject="Mailbox Sync Early Access"
                            />{" "}
                            if you&#39;d like to participate in the early release.
                        </span>
                    </li>
                ) : mailboxChecklistState !== "disconnected" ? (
                    <li className="flex items-start gap-2">
                        <CheckCircleIcon
                            className={`size-5 mt-0.5 shrink-0 ${mailboxConnected ? "text-green-500" : "text-blue-300"}`}
                        />
                        <Link
                            to="/settings/account/email"
                            className={
                                mailboxConnected
                                    ? "text-blue-400 line-through"
                                    : "text-blue-800 underline"
                            }>
                            Connect your email to sync your inbox
                        </Link>
                    </li>
                ) : null}
            </ul>
            <p className="mt-3">
                Contact <SupportEmailLink label="support" subject="Messaging Setup" /> if
                you need help getting started.
            </p>
        </InlineBanner>
    );
};

const MailboxDisconnectedBanner: React.FC = () => {
    return (
        <InlineBanner type="error">
            <p>
                Your mailbox is disconnected, so new emails aren&#39;t being added to your
                records.{" "}
                <Link to="/settings/account/email" className="font-medium underline">
                    Reconnect your mailbox
                </Link>{" "}
                to resume syncing.
            </p>
        </InlineBanner>
    );
};

export const MessagingDashboard: React.FC = () => {
    const { threadId } = useParams<{ threadId: string }>();
    const mailboxSyncEnabled = useFeature("mailboxSync");
    const { hasActiveMailbox, connectionState } = useUserMailboxes();
    const phoneLines = usePhoneLines();

    const phoneLinesConfigured = phoneLines.length > 0;
    const hasAnyChannel = hasActiveMailbox || phoneLinesConfigured;

    const mailboxChecklistState: MailboxChecklistState = mailboxSyncEnabled
        ? connectionState
        : "sync-disabled";

    const showDisconnectedBanner = mailboxChecklistState === "disconnected";
    const showChecklist =
        !phoneLinesConfigured ||
        mailboxChecklistState === "not-connected" ||
        mailboxChecklistState === "sync-disabled";

    if (!hasAnyChannel) {
        return (
            <Stage>
                <PageHeader title="Messages" />
                <ContentContainer className="flex flex-col">
                    {showDisconnectedBanner ? <MailboxDisconnectedBanner /> : null}
                    <MessagingSetupChecklist
                        phoneLinesConfigured={false}
                        mailboxChecklistState={mailboxChecklistState}
                    />
                </ContentContainer>
            </Stage>
        );
    }

    return (
        <Stage>
            <PageHeader title="Latest conversations" />
            <ContentContainer className="flex flex-col">
                {showDisconnectedBanner ? <MailboxDisconnectedBanner /> : null}
                {showChecklist ? (
                    <MessagingSetupChecklist
                        phoneLinesConfigured={phoneLinesConfigured}
                        mailboxChecklistState={mailboxChecklistState}
                    />
                ) : null}
                <RecentConversationsFeed initialThreadId={threadId} />
            </ContentContainer>
        </Stage>
    );
};

interface RecentConversationsFeedProps {
    initialThreadId: string | null;
}

const RecentConversationsFeed: React.FC<RecentConversationsFeedProps> = props => {
    const { initialThreadId } = props;
    const regionId = useRegionId();
    const unifiedMessagingTabEnabled = useFeature("unifiedMessagingTab");

    const query = useGetRecentConversationFeedForUserQuery({ regionId });

    return (
        <QueryRenderer
            query={query}
            name="RecentConversationsFeed.getRecentConversationFeedForUser">
            {data =>
                unifiedMessagingTabEnabled ? (
                    <ConversationFeed
                        feed={data.getRecentConversationFeedForUser}
                        initialThreadId={initialThreadId}
                        mode={{ kind: "inbox" }}
                    />
                ) : (
                    <ConversationFeedLegacy
                        feed={data.getRecentConversationFeedForUser}
                        initialThreadId={initialThreadId}
                    />
                )
            }
        </QueryRenderer>
    );
};
